import type { AirportConfig, GroundHandler, Stand, StandClosure, StandPreference, Turn } from "./types";
import { MIN, localHour, overlaps } from "./time";
import { bestTime } from "./turns";

/** Cost weights shared by the optimizer, scoring and explanations. */
export const COST = {
  HARD: 10_000,
  UNALLOCATED: 5_000,
} as const;

export interface Reason {
  code: string;
  text: string;
}

/** Why a turn can never use a stand, regardless of what else is on the apron. */
export function staticBlock(turn: Turn, stand: Stand, config: AirportConfig): Reason | null {
  if (!stand.active) return { code: "inactive", text: `${stand.name} is not in service` };
  const type = turn.aircraftType ? config.aircraftTypes[turn.aircraftType] : undefined;
  if (type && type.wingspan > stand.maxWingspan + 1e-6) {
    return {
      code: "size",
      text: `${type.code} wingspan ${type.wingspan.toFixed(1)} m exceeds ${stand.name} limit ${stand.maxWingspan} m`,
    };
  }
  if (stand.allowedAirlines?.length && !stand.allowedAirlines.includes(turn.airline)) {
    const names = stand.allowedAirlines.map((a) => config.airlines[a]?.name ?? a).join(" and ");
    return { code: "airline", text: `${stand.name} takes ${names} only` };
  }
  if (stand.allowedTypes?.length && turn.aircraftType && !stand.allowedTypes.includes(turn.aircraftType)) {
    return { code: "type", text: `${stand.name} takes ${stand.allowedTypes.join(", ")} only` };
  }
  if (needsCustoms(turn) && !stand.international) {
    return { code: "international", text: `${stand.name} is not a customs-cleared gate, and an international or transborder arrival clears customs` };
  }
  if (turn.sector === "D" && stand.domesticExcluded) {
    return { code: "international", text: `${stand.name} is reserved for international flights` };
  }
  return null;
}

/**
 * Whether this part of a visit needs a customs-cleared gate: it brings international or
 * transborder passengers in. Customs is for arriving passengers only, so a departure, of
 * any sector, may leave from any gate: an aircraft that arrives domestic and leaves for the
 * US, and one towed off a customs gate after its passengers have cleared, need no customs
 * gate for the departure. The part of a visit after a tow, or the departure half of a split
 * one, carries no arrival passengers.
 */
export function needsCustoms(turn: Turn): boolean {
  return !!turn.arr && turn.arr.sector !== "D" && turn.segment !== "departure" && turn.segment !== "towed";
}

/** Minutes, from Settings, for the customs walk rule; the defaults are YLW's. */
function walkTimes(config: AirportConfig) {
  const s = config.settings;
  return {
    clear: (s.customsClearMin ?? 30) * MIN,
    before: (s.customsDomesticBeforeMin ?? 60) * MIN,
    board: (s.boardingMin ?? 30) * MIN,
  };
}

export interface PassengerWindows {
  /** International or transborder passengers walking to customs: arrival to `customsClearMin` after. */
  uncleared: [number, number] | null;
  /** Passengers who have cleared or never needed to: a domestic arrival unloading, and boarding. */
  cleared: Array<{ from: number; to: number; what: "arrival" | "boarding" }>;
}

/**
 * When this visit has passengers moving at its gate. A domestic arrival counts from its
 * arrival for `customsDomesticBeforeMin` (YLW: an international arrival at least 60 minutes
 * after it finds the gate area clear); boarding is the `boardingMin` before departure, for a
 * departure of any sector. An aircraft simply parked has no window at all.
 */
export function passengerWindows(turn: Turn, config: AirportConfig): PassengerWindows {
  const w = walkTimes(config);
  const out: PassengerWindows = { uncleared: null, cleared: [] };
  if (turn.arr && turn.segment !== "departure" && turn.segment !== "towed") {
    const a = bestTime(turn.arr);
    if (turn.arr.sector !== "D") out.uncleared = [a, a + w.clear];
    else out.cleared.push({ from: a, to: a + w.before, what: "arrival" });
  }
  if (turn.dep && turn.dep.status !== "cancelled" && turn.segment !== "arrival") {
    const d = bestTime(turn.dep);
    out.cleared.push({ from: d - w.board, to: d, what: "boarding" });
  }
  return out;
}

/**
 * The customs walk: two visits on different customs gates whose passengers would meet,
 * uncleared arrivals walking to the customs hall past cleared passengers. Null when they
 * would not. `intl` is the international or transborder arrival, `other` the visit it meets.
 */
export function customsWalkClash(
  a: Turn,
  b: Turn,
  config: AirportConfig,
): { intl: Turn; other: Turn; what: "arrival" | "boarding"; from: number; to: number } | null {
  if ((config.settings.customsClearMin ?? 30) <= 0) return null;
  const wa = passengerWindows(a, config);
  const wb = passengerWindows(b, config);
  for (const [intl, other, wi, wo] of [
    [a, b, wa, wb],
    [b, a, wb, wa],
  ] as const) {
    if (!wi.uncleared) continue;
    const [from, to] = wi.uncleared;
    const hit = wo.cleared.find((c) => c.from < to && from < c.to);
    if (hit) return { intl, other, what: hit.what, from, to };
  }
  return null;
}

/** The customs-cleared gates, whose arrivals walk past each other to the customs hall. */
export function customsGates(config: AirportConfig): Set<string> {
  return new Set(config.stands.filter((s) => s.active && s.international != null).map((s) => s.id));
}

/**
 * Types that are not jets, for a saved configuration whose types predate the `engine`
 * field: the saved list replaces the shipped one, so the shipped field would never reach it.
 */
const NOT_JETS = new Set(["DH8A", "DH8B", "DH8C", "DH8D", "AT43", "AT45", "AT72", "AT76", "B190", "BE20", "BE99", "SF34", "SW4", "PC12", "C208", "DHC6", "JS32", "D328"]);

/** Is this type a jet? Its `engine` when set, else read from the code. Unknown = no. */
export function isJet(code: string | null | undefined, config: AirportConfig): boolean {
  if (!code) return false;
  const engine = config.aircraftTypes[code]?.engine;
  if (engine) return engine === "jet";
  return !!config.aircraftTypes[code] && !NOT_JETS.has(code);
}

/** The ground handler working an airline's flights: the one naming it, else the one that takes everyone else. */
export function handlerOf(airline: string, config: AirportConfig): GroundHandler | null {
  const hs = config.groundHandlers ?? [];
  return hs.find((h) => h.airlines.includes(airline)) ?? hs.find((h) => h.airlines.length === 0) ?? null;
}

/** Is this one of the airlines whose aircraft are kept together? At YLW, Air Canada and WestJet. */
export function keptTogether(airline: string, config: AirportConfig): boolean {
  return (config.settings.keepTogetherAirlines ?? ["ACA", "WJA"]).includes(airline);
}

/** Evening from this local hour: an aircraft in by then and out early tomorrow sleeps on a bridge. */
const OVERNIGHT_IN_FROM = 16;
/** Early morning: out before this local hour. */
const OVERNIGHT_OUT_BY = 10;

/**
 * What kind of stay a jet's visit is, which decides whether it wants a bridge:
 * - **turn**: in and out again: a bridge.
 * - **overnight**: domestic, in from the evening and out early next morning: a bridge, as
 *   nothing needs it overnight and the morning boarding is from it.
 * - **overnight-intl**: an international arrival staying the night: a ground-load stand or
 *   Gate 4's bridge, either is fine.
 * - **long-day**: anything else on the ground for hours, such as in at mid-day and out the
 *   next morning: better on a ground-load stand (or towed) than holding a bridge all day.
 */
export type JetStay = "turn" | "overnight" | "overnight-intl" | "long-day";

export function jetStay(turn: Turn, config: AirportConfig): JetStay {
  const tz = config.timezone;
  const long = (config.settings.jetLongStayMin ?? 180) * MIN;
  if (turn.ron) {
    if (needsCustoms(turn)) return "overnight-intl";
    const inEvening = !turn.arr || localHour(turn.start, tz) >= OVERNIGHT_IN_FROM;
    const outEarly = !turn.dep || localHour(turn.end, tz) < OVERNIGHT_OUT_BY;
    return inEvening && outEarly ? "overnight" : "long-day";
  }
  return turn.end - turn.start > long ? "long-day" : "turn";
}

/** A jet whose stay wants a jet bridge: a turn, or a domestic overnight from the evening. */
export function wantsBridge(turn: Turn, config: AirportConfig): boolean {
  if (!isJet(turn.aircraftType, config)) return false;
  const k = jetStay(turn, config);
  return k === "turn" || k === "overnight";
}

/** A small turboprop (Beech 1900, Saab 340, Metro): not one for a jet bridge gate. */
export function isSmallProp(code: string | null | undefined, config: AirportConfig): boolean {
  return !!code && (config.settings.smallPropTypes ?? ["B190", "BE20", "SF34", "SW4"]).includes(code);
}

/** Each contact stand's place along the terminal, for how far apart two stands are. */
export function contactOrder(config: AirportConfig): Map<string, number> {
  const row = config.stands.filter((s) => s.kind === "contact" && s.active).sort((a, b) => a.order - b.order);
  return new Map(row.map((s, i) => [s.id, i]));
}

/**
 * The stands a departure from `stand` shares its pushback with: the stand's own list when
 * set, else the active contact stands either side of it in the terminal's order.
 */
export function pushbackNeighbours(stand: Stand, config: AirportConfig): string[] {
  if (stand.pushbackNeighbours) return stand.pushbackNeighbours;
  if (stand.kind !== "contact") return [];
  const row = config.stands.filter((s) => s.kind === "contact" && s.active).sort((a, b) => a.order - b.order);
  const i = row.findIndex((s) => s.id === stand.id);
  if (i < 0) return [];
  return [row[i - 1]?.id, row[i + 1]?.id].filter((id): id is string => !!id);
}

/** The departure time two pushbacks are compared on: the latest estimate, else the schedule. */
export function pushbackAt(turn: Turn): number | null {
  if (!turn.dep || turn.dep.status === "cancelled") return null;
  return turn.dep.actual ?? turn.dep.estimated ?? turn.dep.scheduled;
}

/** Minutes between two departures' pushbacks, when they are close enough to hold each other up. */
export function pushbackClash(a: Turn, b: Turn, config: AirportConfig): number | null {
  const spacing = config.settings.pushbackSpacingMin ?? 0;
  if (spacing <= 0) return null;
  const x = pushbackAt(a);
  const y = pushbackAt(b);
  if (x == null || y == null) return null;
  const gap = Math.abs(x - y) / MIN;
  return gap < spacing ? Math.round(gap) : null;
}

export interface CostPart {
  code: string;
  cost: number;
  text: string;
}

/**
 * Does a preference cover this turn: by airline or by one of its flight numbers, and
 * then by what is being flown. A type the feed did not give us and we guessed still
 * counts - the guess is the airport's own rule for that airline, written down in
 * Settings, and a rule that ignored it would quietly send every unknown Q400 to a bridge.
 */
export function preferenceCovers(pref: StandPreference, turn: Turn): boolean {
  const who = pref.flights?.length
    ? [turn.arr?.flightNumber, turn.dep?.flightNumber].some((f) => !!f && pref.flights!.includes(f))
    : pref.airlines.includes(turn.airline);
  if (!who) return false;
  const type = turn.aircraftType;
  if (pref.aircraftTypes?.length) return !!type && pref.aircraftTypes.includes(type);
  if (pref.exceptAircraftTypes?.length) return !type || !pref.exceptAircraftTypes.includes(type);
  return true;
}

/** Soft costs that depend only on the turn and the stand. */
export function staticCostParts(turn: Turn, stand: Stand, config: AirportConfig): CostPart[] {
  const parts: CostPart[] = [];
  const s = config.settings;
  if (stand.kind !== "contact") {
    parts.push({ code: "remote", cost: s.remotePenalty, text: `${stand.name} is a remote stand (passengers bussed or walked)` });
  }
  if (!stand.jetbridge && (s.jetNoBridgePenalty ?? 0) > 0 && wantsBridge(turn, config)) {
    parts.push({ code: "jet-no-bridge", cost: s.jetNoBridgePenalty!, text: `${stand.name} has no jet bridge for a jet` });
  }
  // A jet in for hours by day holds the bridge from the turns that need it; ground load suits it.
  if (stand.jetbridge && (s.jetLongStayOnBridgePenalty ?? 150) > 0 && isJet(turn.aircraftType, config) && jetStay(turn, config) === "long-day") {
    const mins = Math.round((turn.end - turn.start) / MIN);
    parts.push({
      code: "long-jet-on-bridge",
      cost: s.jetLongStayOnBridgePenalty ?? 150,
      text: `A jet on the ground ${mins >= 120 ? `${Math.round(mins / 60)} h` : `${mins} min`}, not an evening-to-morning overnight, holds ${stand.name}'s jet bridge; it belongs on a ground-load stand`,
    });
  }
  if (stand.jetbridge && (s.smallPropOnBridgePenalty ?? 300) > 0 && isSmallProp(turn.aircraftType, config)) {
    parts.push({ code: "small-prop-on-bridge", cost: s.smallPropOnBridgePenalty ?? 300, text: `A ${turn.aircraftType} is a small turboprop on ${stand.name}, a jet bridge gate` });
  }
  if (needsCustoms(turn) && stand.international === "fallback") {
    parts.push({ code: "intl-fallback", cost: s.intlFallbackPenalty, text: `${stand.name} is the fallback international gate` });
  }
  for (const pref of config.preferences) {
    if (!preferenceCovers(pref, turn)) continue;
    const met =
      (pref.prefer.jetbridge === undefined || stand.jetbridge === pref.prefer.jetbridge) &&
      (pref.prefer.stands === undefined || pref.prefer.stands.includes(stand.id));
    if (!met) parts.push({ code: `pref:${pref.id}`, cost: pref.weight, text: `Misses preference: ${pref.label}` });
  }
  // A turboprop on the ground for hours keeps a jet bridge from the jets that need it.
  const limit = s.longPropOnBridgeMin ?? 90;
  if (stand.jetbridge && turn.arr && turn.dep && turn.aircraftType && !isJet(turn.aircraftType, config) && (s.longPropOnBridgePenalty ?? 0) > 0) {
    const mins = Math.round((turn.end - turn.start) / MIN);
    if (mins > limit) {
      parts.push({
        code: "long-prop-on-bridge",
        cost: s.longPropOnBridgePenalty!,
        text: `A ${turn.aircraftType} on the ground ${mins} min holds ${stand.name}'s jet bridge; over ${limit} min it belongs on a gate without one`,
      });
    }
  }
  const handler = handlerOf(turn.airline, config);
  if (handler?.homeStands.length && !handler.homeStands.includes(stand.id) && (s.handlerHomePenalty ?? 0) > 0) {
    parts.push({ code: "handler-home", cost: s.handlerHomePenalty!, text: `${stand.name} is away from where ${handler.name} keeps its equipment` });
  }
  const type = turn.aircraftType ? config.aircraftTypes[turn.aircraftType] : undefined;
  if (type && stand.maxWingspan - type.wingspan > 12) {
    parts.push({ code: "oversize", cost: s.oversizePenalty, text: `Small aircraft on a large stand` });
  }
  return parts;
}

export function staticCost(turn: Turn, stand: Stand, config: AirportConfig): number {
  let c = 0;
  for (const p of staticCostParts(turn, stand, config)) c += p.cost;
  return c;
}

/** Does the preference list apply to this airline, and is it met on this stand? */
export function preferenceStatus(turn: Turn, stand: Stand | undefined, config: AirportConfig): { total: number; met: number } {
  let total = 0;
  let met = 0;
  for (const pref of config.preferences) {
    if (!preferenceCovers(pref, turn)) continue;
    total++;
    if (!stand) continue;
    const ok =
      (pref.prefer.jetbridge === undefined || stand.jetbridge === pref.prefer.jetbridge) &&
      (pref.prefer.stands === undefined || pref.prefer.stands.includes(stand.id));
    if (ok) met++;
  }
  return { total, met };
}

export function closureHit(turn: Turn, standId: string, closures: StandClosure[]): StandClosure | null {
  for (const c of closures) {
    if (c.stand === standId && overlaps(turn.start, turn.end, c.from, c.to)) return c;
  }
  return null;
}

/** Gap in minutes between two turns on the same stand (negative = overlap). */
export function gapMin(a: Turn, b: Turn): number {
  const [first, second] = a.start <= b.start ? [a, b] : [b, a];
  return (second.start - first.end) / MIN;
}

/**
 * Does `turnA` on `standA` keep `turnB` off `standB`? A dependency can be about what is
 * flying (a 738 on 1 reaches into 2) or who is on board (an international flight on 4
 * closes 1 to 3 to domestic flights). Without `turnB` the sector of the blocked flight
 * is not known, and the rule is read as applying to all of them.
 */
export function dependencyApplies(config: AirportConfig, standA: string, turnA: Turn, standB: string, turnB?: Turn): boolean {
  for (const d of config.dependencies) {
    if (d.stand !== standA || d.blocks !== standB) continue;
    if (d.whenTypes.length && !(turnA.aircraftType && d.whenTypes.includes(turnA.aircraftType))) continue;
    if (d.whenSectors?.length && !d.whenSectors.includes(turnA.sector)) continue;
    if (turnB && d.blocksSectors?.length && !d.blocksSectors.includes(turnB.sector)) continue;
    return true;
  }
  return false;
}
